/**
 * Boot timing marks for the app's three instrumented cold-boot paths: the
 * Shopify App Pricing return flow, the dashboard at `/`, and the campaign-editor
 * modal realm.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY performance.mark AND NOT console.*
 * ─────────────────────────────────────────────────────────────────────────────
 * vite.config.mjs:13-15 sets `esbuild.pure` to ['console.log', 'console.warn',
 * 'console.debug', 'console.info'] with build.minify: 'esbuild', so those call
 * sites are DELETED AT BUILD TIME. Production is the only environment where
 * these numbers matter, so any console-based instrumentation here would emit
 * nothing exactly when it is needed.
 *
 * (Corollary worth knowing: main.jsx:75-80's careful "[Nudgesmart]"-prefix
 * filter for console.warn is unreachable dead code in production for the same
 * reason — the calls never survive to be filtered.)
 *
 * performance.mark is a real API call with side effects, so `pure` leaves it
 * alone. It also needs no exposure plumbing: read it in any deployed build with
 *
 *     performance.getEntriesByType('measure').filter(m => m.name.startsWith('ns:'))
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY EVERY measure() IS GUARDED
 * ─────────────────────────────────────────────────────────────────────────────
 * performance.measure() THROWS a SyntaxError if a named start or end mark does
 * not exist, and `measureBoot` is a general-purpose export — nothing constrains
 * a future caller to a point where both marks are guaranteed.
 *
 * (measurePlanActivationBoot, called from welcome.jsx's `finally`, happens to
 * run where every mark is already set: `sync_resolved` is marked on the line
 * above it, `welcome_mount` in that component's own render, and `auth_ready`
 * before the route could mount at all. So on THAT path the guards are
 * belt-and-braces. Do NOT take that as licence to remove them — an earlier
 * version of this comment justified them with cases that cannot occur, and a
 * guard defended by a false rationale is one "simplification" away from being
 * deleted. measureEditorBoot below has no such luxury: its marks are set by
 * several components across a deliberately parallelised boot, so missing marks
 * there are a NORMAL outcome, not an anomaly.)
 *
 * The real point: an uncaught throw here would crash the render of the exact
 * page this instrumentation exists to make reliable. Measurement must never be
 * able to break the thing it measures.
 */

const PREFIX = "ns:";

function hasMark(name) {
  try {
    return performance.getEntriesByName(`${PREFIX}${name}`, "mark").length > 0;
  } catch {
    return false;
  }
}

/**
 * Record a boot milestone. Safe to call anywhere, including during render.
 *
 * ⚠️ IDEMPOTENT BY DESIGN — the first call for a name wins.
 *
 * `auth_ready` is marked in AuthenticatedApp's RENDER path, which runs again on
 * every re-render (and twice per mount under StrictMode). Without this guard the
 * mark would keep moving forward, so it would report the most recent render
 * rather than the moment auth completed — quietly turning every downstream
 * measure into a smaller, wrong number. These are one-time boot milestones by
 * definition, so "first write wins" is the correct semantic, not a workaround.
 */
export function markBoot(name) {
  if (hasMark(name)) return;
  try {
    performance.mark(`${PREFIX}${name}`);
  } catch {
    // performance API unavailable — instrumentation is never load-bearing.
  }
}

/**
 * Create a named measure between two marks, skipping silently if either is
 * missing. Returns the duration in ms, or null.
 */
export function measureBoot(name, startMark, endMark) {
  if (!hasMark(startMark) || !hasMark(endMark)) return null;
  try {
    const entry = performance.measure(
      `${PREFIX}${name}`,
      `${PREFIX}${startMark}`,
      `${PREFIX}${endMark}`
    );
    return entry?.duration ?? null;
  } catch {
    return null;
  }
}

/**
 * Segment the App Pricing return once the sync has settled.
 *
 * Marks are timed from navigation start, so `ns:auth_ready` doubles as
 * "everything before auth completed" without needing a t0 mark in index.html.
 */
export function measurePlanActivationBoot() {
  // No measure for "navigation start → auth ready": a mark's own `startTime` is
  // already ms since navigation start, so `ns:auth_ready`.startTime IS that
  // number. Measuring a mark against itself would just report 0.
  measureBoot("auth_to_welcome", "auth_ready", "welcome_mount");
  measureBoot("welcome_to_dispatch", "welcome_mount", "sync_dispatch");
  measureBoot("sync_roundtrip", "sync_dispatch", "sync_resolved");
  measureBoot("total_to_settled", "auth_ready", "sync_resolved");
}

/**
 * Segment the DASHBOARD boot — the `/` route in the top-level admin frame.
 *
 * This is the path a merchant actually opens the app on, and until now it was
 * the only instrumented cold boot with no aggregator: the marks were already
 * being set, but nothing measured the segments BETWEEN them, so the four
 * serial round-trips the boot is made of were inferred rather than observed.
 *
 * Unlike measureEditorBoot below, these marks are in the TOP frame's
 * performance timeline (the editor lives in an App Bridge Modal iframe with its
 * own timeline). Read them from the top frame.
 *
 * Marks and who sets them:
 *   boot_entry       main.jsx — earliest point in this document
 *   auth_ready       components/App.jsx, AuthenticatedApp render
 *   shop_ready       providers/ShopProvider.jsx render, first render with a shop
 *   dashboard_mount  routes/index.jsx, dashboard render body
 *   campaigns_ready  routes/index.jsx, campaigns query settled
 *
 * ⚠️ `auth_to_shop`, `auth_to_campaigns` and `auth_to_dashboard_mount` are ALL
 * anchored at auth_ready ON PURPOSE — do NOT "tidy" any of them into a chain of
 * shop → campaigns. The shop query is a live WebSocket subscription, the
 * campaigns query is plain HTTP, and the route chunk starts downloading at
 * module eval; they are parallel branches from a common fork, so their order is
 * a race and chaining them reports a NEGATIVE duration whichever way it lands.
 * This is the same hazard measureEditorBoot documents for auth_to_shop /
 * auth_to_editor_mount — same fork, same rule.
 *
 * Corollary: when the numbers look impossible, read the marks as a TIMELINE
 * rather than as durations —
 *   performance.getEntriesByType('mark')
 *     .filter(m => m.name.startsWith('ns:'))
 *     .map(m => `${m.name}: ${Math.round(m.startTime)}ms`)
 *
 * Every segment is optional by construction: measureBoot returns null rather
 * than throwing when either mark is missing (performance.measure throws a
 * SyntaxError on a missing mark). A boot that short-circuits — no shop, an error
 * page, a deep link straight into the modal — legitimately never sets some of
 * these, and a partial timeline must degrade to fewer measures, never to a
 * crashed dashboard render.
 */
export function measureDashboardBoot() {
  // No measure for "navigation start → boot entry": a mark's own startTime is
  // already ms since navigation start, so ns:boot_entry.startTime IS that number.
  measureBoot("entry_to_auth", "boot_entry", "auth_ready");

  // ⚠️ ALL THREE anchored at auth_ready, never chained — see the warning above.
  // shop is the WebSocket branch, campaigns is the HTTP branch, and
  // dashboard_mount is the route-chunk branch (it starts at module eval, not
  // after any query). Three siblings, one fork.
  measureBoot("auth_to_shop", "auth_ready", "shop_ready");
  measureBoot("auth_to_campaigns", "auth_ready", "campaigns_ready");
  // The honest "how long did the dashboard's JS take to arrive" number, and the
  // one §5c's route-chunk modulepreload is trying to move. Mirrors the editor's
  // auth_to_editor_mount for the same reason.
  measureBoot("auth_to_dashboard_mount", "auth_ready", "dashboard_mount");

  // ⚠️ READ THIS AS A TIMELINE, NOT AS A DURATION — same caveat as
  // campaign_to_chunks below. `dashboard_mount` is fired from the route's render
  // BODY, which React runs (and re-runs) well before the campaigns query
  // settles, so on most boots this is NEGATIVE. That is not a bug, it is the
  // answer: the dashboard's code was already there and the whole wait was data.
  // It only goes positive when the route chunk is the laggard — which is
  // precisely the regression a preload change could introduce, so it is worth
  // emitting even though its usual value is a negative number.
  measureBoot("campaigns_to_paint", "campaigns_ready", "dashboard_mount");

  // End the total at campaigns_ready, not at dashboard_mount: mount is when the
  // shell of the route exists, campaigns_ready is when there is something real
  // to show. Decision rule in the plan (§Step 0) is read off the segments above,
  // not off this — it is here for a single before/after headline.
  measureBoot("total_dashboard_boot", "boot_entry", "campaigns_ready");
}

/**
 * Segment the campaign-editor boot.
 *
 * This runs in the App Bridge MODAL'S OWN REALM (routes/index.jsx renders
 * <Modal src="/popup?campaignId=…">), which is a separate iframe with a separate
 * document and a separate performance timeline. So these marks never mix with
 * the dashboard's, and every number here is relative to the MODAL's navigation
 * start — read them from the modal frame, not the top frame.
 *
 * Marks and who sets them:
 *   boot_entry         index.html / entry — earliest point in this document
 *   auth_ready         components/App.jsx, AuthenticatedApp render
 *   shop_ready         providers/ShopProvider.jsx render, first render with a shop
 *   editor_mount       routes/popup.jsx, editor render
 *   campaign_ready     routes/popup.jsx, campaign query settled
 *   chunks_ready       routes/popup.jsx, BOTH editor sub-chunks resolved. Races
 *                      campaign_ready — it starts at module eval, not after the
 *                      query — so its order relative to campaign_ready is not
 *                      guaranteed and is itself the interesting signal.
 *   canvas_first_paint Preview, first artboard paint
 *   editor_visible     cover lifted / editor interactive
 *
 * ⚠️ EVERY SEGMENT IS OPTIONAL BY CONSTRUCTION. measureBoot returns null rather
 * than throwing when either mark is missing (performance.measure throws a
 * SyntaxError on a missing mark), which matters far more here than on the plan
 * activation path: the whole POINT of the early-mount change is that shop_ready
 * and editor_mount now race, so their ORDER is not guaranteed and a boot that
 * short-circuits will legitimately be missing marks. A partial timeline must
 * degrade to fewer measures, never to a crashed editor render.
 *
 * Corollary of that race: `auth_to_shop` and `auth_to_editor_mount` are both
 * anchored at auth_ready ON PURPOSE, rather than chaining shop → editor. They
 * are two parallel branches from a common fork, and chaining them would report a
 * negative or nonsensical duration whichever way the race lands.
 */
export function measureEditorBoot() {
  // As above, no measure for "navigation start → boot entry": a mark's own
  // startTime is already ms since navigation start.
  measureBoot("entry_to_auth", "boot_entry", "auth_ready");
  measureBoot("auth_to_shop", "auth_ready", "shop_ready");
  measureBoot("auth_to_editor_mount", "auth_ready", "editor_mount");
  measureBoot("editor_to_campaign", "editor_mount", "campaign_ready");
  measureBoot("campaign_to_canvas", "campaign_ready", "canvas_first_paint");
  measureBoot("canvas_to_visible", "canvas_first_paint", "editor_visible");
  measureBoot("total_editor_boot", "boot_entry", "editor_visible");

  // ── Decomposition of campaign_to_canvas ───────────────────────────────────
  // campaign_to_canvas measured 4175ms on 2026-08-16 — 66% of the whole boot,
  // and it is a BLACK BOX: it bundles the Preview/PopupSettings chunk download,
  // the element-tree render, the iframe srcDoc navigation and its load event.
  // Those need completely different fixes, so splitting it comes before any
  // optimisation.
  //
  // ⚠️ READ THESE AS A TIMELINE, NOT AS DURATIONS. `chunks_ready` is fired from
  // a module-scope import() in routes/popup.jsx, so it races the campaign query
  // rather than following it — on a warm HTTP cache it can land BEFORE
  // campaign_ready, which makes `campaign_to_chunks` negative. That is not a
  // bug, it is the answer: chunks were never the bottleneck on that boot and
  // the whole 4175ms is render. Compare the marks' startTime when in doubt:
  //   performance.getEntriesByType('mark')
  //     .filter(m => m.name.startsWith('ns:'))
  //     .map(m => `${m.name}: ${Math.round(m.startTime)}ms`)
  measureBoot("campaign_to_chunks", "campaign_ready", "chunks_ready");
  measureBoot("chunks_to_canvas", "chunks_ready", "canvas_first_paint");
  // Anchored at editor_mount, not campaign_ready, because the import() starts at
  // module eval — this is the honest "how long did the editor's JS take to
  // arrive" number regardless of how the race lands.
  measureBoot("mount_to_chunks", "editor_mount", "chunks_ready");
}
